/**
 * Coach Profile — Drift Detection
 *
 * Compare two CoachProfile snapshots (prior + current) and produce a
 * diff describing what's new, what persisted, what resolved, and what
 * shifted in priority. The diff is what powers the "Coach has been
 * watching" narrative — without it, every dashboard render feels
 * stateless.
 *
 * Vocabulary:
 *   - "new"        — entry signature is in current but NOT in prior
 *   - "persisted"  — signature is in BOTH (Coach has been flagging it)
 *   - "resolved"   — signature was in prior LEAKS but NOT in current
 *                    (the trader fixed it OR it temporarily stopped firing)
 *   - "regressed"  — signature was in prior EDGES but is now in current
 *                    LEAKS (an edge turned into a leak)
 *   - "ascended"   — signature was in prior LEAKS but is now in current
 *                    EDGES (a leak became an edge — usually after fixing
 *                    a calibration mismatch)
 *
 * Pure: takes two profiles, returns a diff. No I/O, no time-of-day logic.
 */

const buildSignatureMaps = (profile) => {
  const edges = new Map();
  const leaks = new Map();
  if (!profile) return { edges, leaks };
  for (const e of (profile.edges || [])) edges.set(e.signature, e);
  for (const l of (profile.leaks || [])) leaks.set(l.signature, l);
  return { edges, leaks };
};

/**
 * @param {CoachProfile|null} prior
 * @param {CoachProfile} current
 * @returns {ProfileDiff}
 */
export const diffProfile = (prior, current) => {
  if (!current) {
    return {
      newEdges: [], newLeaks: [],
      persistedEdges: [], persistedLeaks: [],
      resolvedLeaks: [], regressed: [], ascended: [],
      hasPrior: false,
    };
  }
  // No prior to diff against — return an empty drift report. The dashboard
  // uses hasPrior to know whether to render "Since last snapshot…" copy or
  // a first-snapshot welcome state.
  if (!prior) {
    return {
      newEdges: [], newLeaks: [],
      persistedEdges: [], persistedLeaks: [],
      resolvedLeaks: [], regressed: [], ascended: [],
      hasPrior: false,
    };
  }

  const priorMaps = buildSignatureMaps(prior);
  const curMaps = buildSignatureMaps(current);

  const newEdges = [];
  const newLeaks = [];
  const persistedEdges = [];
  const persistedLeaks = [];
  const regressed = [];
  const ascended = [];

  // Walk current edges
  for (const [sig, entry] of curMaps.edges) {
    if (priorMaps.edges.has(sig)) {
      // Was an edge, still an edge → persisted
      persistedEdges.push({
        ...entry,
        priorPriority: priorMaps.edges.get(sig).priority || 0,
      });
    } else if (priorMaps.leaks.has(sig)) {
      // Was a leak, now an edge → ascended
      ascended.push({
        ...entry,
        priorEntry: priorMaps.leaks.get(sig),
      });
    } else {
      newEdges.push(entry);
    }
  }

  // Walk current leaks
  for (const [sig, entry] of curMaps.leaks) {
    if (priorMaps.leaks.has(sig)) {
      const priorEntry = priorMaps.leaks.get(sig);
      persistedLeaks.push({
        ...entry,
        priorPriority: priorEntry.priority || 0,
        // Persistence count: how many runs in a row this leak has shown up.
        // We can't recompute true "weeks running" without history; the
        // occurrences field carried forward in entry already does this for us.
      });
    } else if (priorMaps.edges.has(sig)) {
      // Was an edge, now a leak → regressed
      regressed.push({
        ...entry,
        priorEntry: priorMaps.edges.get(sig),
      });
    } else {
      newLeaks.push(entry);
    }
  }

  // Resolved leaks — any prior leak that's not in current leaks (and not
  // ascended either, since ascended is its own category).
  // current.resolvedLeaks already lists these from buildProfile — use it
  // when present so we get the resolvedAt timestamp + carried-forward
  // history. Otherwise compute from the maps.
  const resolvedLeaks = Array.isArray(current.resolvedLeaks) && current.resolvedLeaks.length > 0
    ? current.resolvedLeaks
    : (() => {
        const out = [];
        for (const [sig, prior] of priorMaps.leaks) {
          if (!curMaps.leaks.has(sig) && !curMaps.edges.has(sig)) {
            out.push({ ...prior, resolvedAt: current.generatedAt });
          }
        }
        return out;
      })();

  return {
    newEdges, newLeaks,
    persistedEdges, persistedLeaks,
    resolvedLeaks,
    regressed, ascended,
    hasPrior: !!prior,
  };
};

/**
 * Convenience: a one-line summary of the diff for narration purposes.
 * Returns a sentence the dashboard can show under the title.
 */
export const summarizeDiff = (diff) => {
  if (!diff || !diff.hasPrior) return 'First profile snapshot — no prior to compare.';
  const parts = [];
  if (diff.newEdges.length > 0) parts.push(`${diff.newEdges.length} new edge${diff.newEdges.length === 1 ? '' : 's'}`);
  if (diff.newLeaks.length > 0) parts.push(`${diff.newLeaks.length} new leak${diff.newLeaks.length === 1 ? '' : 's'}`);
  if (diff.resolvedLeaks.length > 0) parts.push(`${diff.resolvedLeaks.length} leak${diff.resolvedLeaks.length === 1 ? '' : 's'} resolved`);
  if (diff.ascended.length > 0) parts.push(`${diff.ascended.length} ascended`);
  if (diff.regressed.length > 0) parts.push(`${diff.regressed.length} regressed`);
  if (parts.length === 0) {
    const pe = diff.persistedEdges.length;
    const pl = diff.persistedLeaks.length;
    return `No drift since last snapshot — ${pe} edge${pe === 1 ? '' : 's'} and ${pl} leak${pl === 1 ? '' : 's'} are stable.`;
  }
  return `Since last snapshot: ${parts.join(', ')}.`;
};

// Exports for tests
export { buildSignatureMaps };
